<template>

  <div>

    <approval-remarks :row-data="actionData" :steps="actionData.steps" :legal-step="3" />

    <section v-if="isBackhaul">

        <!-- XOL-552: "+ Backhaul" sits above the site's line table so a Tower Co
             user can add the missing line without leaving the RTB. It opens the
             full BackhaulProvider form in new mode, stamped with this RTB, with
             Provider = the telco's Backhaul twin and Type = the RTB's type.
             `sharer-id` is deliberately NOT passed: it would flip the popup to
             "Customer". `:table-ref="null"` stops the stray
             backhaultable-refresh-table broadcast. The new line is not
             auto-selected — the list refreshes and the user picks. -->
        <template v-if="isTowerCo() && actionData.subActionType === 'CUST'">
          <div class="text-right">
            <backhaul-action
              :provider="true"
              button-text="Backhaul"
              :site-id="actionData.site_id"
              :site-name="site ? site.siteName : null"
              :action-id="actionData.id"
              :default-provider-id="telcoBackhaulTwinId"
              :default-backhaul-type="actionData.backhaulType"
              :read-only="readOnly"
              :table-ref="null"
              close-on-save
              @saved="onBackhaulLineAdded" />
          </div>

          <backhaul-customer
            ref="bhCustomer"
            :sharer-id="actionData.organisation_id"
            :site-id="actionData.site_id"
            :action-id="actionData.id"
            :site-code="site ? site.siteId : null"
            :site-name="site ? site.siteName : null"
            :read-only="readOnly"
            :is-request="true" />
        </template>

        <!-- XOL-526 (C4, UX §2.2): a Telco is no longer mounted here at all —
             the backhaul details are the Tower Co's and the provider's, not
             the telco's. `:editable` and the mount condition are the SAME
             expression: a Tower Co or the provider can land here; nobody
             else does. lockedForViewer inside BackhaulProvider still holds
             the actual editing controls to Tower-Co-only. -->
        <backhaul-provider
          v-else-if="isTowerCo() || isRequestProvider()"
          :provider-id="actionData.provider_id"
          :site-id="actionData.site_id"
          :action-id="actionData.id"
          :editable="isTowerCo() || isRequestProvider()"
          :is-request="true" />

    </section>

    <section v-if="isTowerCo() && canAccess('finance')">

        <el-form :inline="true">
            <el-form-item label="Agreement Type">

              <el-select
                v-model="step3.docType"
                placeholder="Select"
                filterable
                allow-create
                :disabled="!canAccess('finance') || disabled">

                <el-option
                   v-for="item in types"
                   :key="item.value"
                   :label="item.label"
                   :value="item.value">
                 </el-option>

              </el-select>

          </el-form-item>
        </el-form>


      <p>Agreement Type: <b>{{step3.docType}}</b></p>

      <el-collapse v-model="accordion" v-if="!disabled" accordion>

      <el-collapse-item title="Finance Apprvoal of Legal Document" name="FinApprove" v-if="isTowerCo()">

          <el-alert type="info" :title="'Approval of ' + (step3.docType ? step3.docType : 'legal documents')" show-icon :closable="false">
            <check-box v-model="step3.legalApproved" no-form @change="legalChecked">{{(step3.docType ? step3.docType : 'Legal Document')}}</check-box>
            <span v-if="step.approved"><b>{{formatDate(step.approved_at)}}</b> by <b>{{step.approved_by}}</b></span>
          </el-alert>

          <el-alert
          v-if="this.isActive(this.actionData)"
          type="info1"
          title="Progress Remarks"
          show-icon />

          <html-input v-model="step.remarks" :disabled="isViewOnly() || !this.isActive(this.actionData)" />

      </el-collapse-item>

      <el-collapse-item v-if="isTowerCo() && !disabled && step.actionType === 'RTS'" title="Create Sharer" name="Sharer">
        <el-row>
          <h2 v-if="!step3.saved"><strong class="text-success">Add</strong> New Sharer</h2>
          <h3 v-else>Sharer already created</h3>
        </el-row>

        <licensee-form
        :site-id="rowData.site_id"
        :tower-id="site.siteId"
        :sharer-name="actionData.organisation.name"
        :sharer-id="actionData.organisation.id"
        :editable="step3.saved"
        :saved="step3.saved"
        :sharer-site-id="step2p2.state.siteId"
        :service-type="step2p2.state.serviceType"
        :spectrum-band="step2p2.state.spectrumBand"
        :equipment="step2.equipment" />
        <!-- @TODO need to figure out how to handle this: :editable="true" -->
      </el-collapse-item>

    </el-collapse>


    </section>


    <section v-else>

      <div v-if="isTowerCo()">
        <el-alert
          v-if="this.isActive(this.actionData) && step.remarks"
          type="info"
          title="Progress Remarks"
          :closable="false"
          show-icon />

          <html-input v-model="step.remarks" disabled no-form />
        </div>

        <div v-else>
          <el-alert
            v-if="this.isActive(this.actionData)"
            type="info"
            title="Progress Remarks"
            :closable="false"
            show-icon />

            <!-- XOL-526 (D5, S5): was an undisabled html-input — ANY
                 non-Tower-Co viewer (telco or provider) saw a live editor
                 with no gate at all, though a save had nowhere to go (PUT
                 /step was role:towerco pre-API-5). canWriteOwnStep() now
                 offers a real write, folding in the party-1-step-completed
                 lock (D9/A10). -->
            <small v-if="progressRemarksNote" class="text-muted">{{progressRemarksNote}}</small>
            <html-input v-model="step.remarks" :disabled="!canWriteOwnStep(step) || readOnly" no-form />
            <small v-if="progressRemarksStamp" class="text-muted">{{lastUpdatedLine(progressRemarksStamp)}}</small>

        </div>

    </section>

  </div>

</template>

<script>

import RequestMixins from '@/mixins/RequestMixins'

import FileDownload from '@/components/atoms/files/FileDownload'
import FileUpload from '@/components/atoms/files/FileUpload'

import LicenseeForm from '@/components/requests/LicenseeForm'

import BackhaulAction from '@/components/actions/BackhaulAction'
import BackhaulCustomer from '@/components/backhaul/BackhaulCustomer'
import BackhaulProvider from '@/components/backhaul/BackhaulProvider'

import { findBackhaulTwinId } from '@/lib/backhaulTwin'

import ApprovalRemarks from '@/components/requests/workflow/ApprovalRemarks'

export default {

  // XOL-534 (reviewer): DateMixins already arrives transitively via
  // RequestMixins -> SiteMixins — a direct import here was redundant.
  mixins: [RequestMixins],

  components: {
    ApprovalRemarks,
    BackhaulAction,
    BackhaulCustomer,
    BackhaulProvider,
    FileDownload,
    FileUpload,
    LicenseeForm
  },

  props: {

    rowData: {
      type: Object,
      required: true
    },

    rowIndex: {
      type: Number,
      default: null
    },

    stepData: {
      type: Object,
      default: null
    },

    readOnly: {
      type: Boolean,
      default: true
    },

    isClosed: {
      type: Boolean,
      default: false
    }

  },



  methods: {

    // XOL-552: "+ Backhaul" saved a line — re-read the list. Deliberately does
    // NOT select the new row (the user picks it).
    onBackhaulLineAdded () {
      if (this.$refs.bhCustomer) {
        this.$refs.bhCustomer.refreshLines()
      }
    },

    setStep (data) {
      if (data) {
        this.step = data

        // To handle existing RTx without this flag.
        this.step.legalApproved = (this.actionData.status !== 'Closed' ? this.step.legalApproved : true)

        // XOL-526 (S5): re-capture the Progress Remarks dirty baseline off
        // the fresh fetch.
        this.initialRemarks = data.remarks
      }
    },

    legalChecked (check) {

      this.step.approved = check

      if (check) {
        this.step.approved_at = this.formatDateSql(new Date())
        this.step.approved_by = this.userName()
      } else {
        this.step.approved_at = null
        this.step.approved_by = null
      }
    }

  },



  data () {

    return {
      accordion: 'FinApprove',
      actionData: this.rowData,
      site: this.rowData.site,
      step: this.stepData,
      step2: this.rowData.state.step2,
      step2p2: null,
      step3p1: null,
      step3: this.rowData.state.step3,

      // XOL-526 (S5): dirty baseline for the counterparty's own saveOwnStep().
      initialRemarks: null,
      types: [
        {
          value: 'LOO',
          label: 'LOO'
        }, {
          value: 'SLO',
          label: 'SLO'
        }, {
          value: 'AWO',
          label: 'AWO'
        }, {
          value: 'AAWO',
          label: 'AAWO'
        }, {
          value: 'AROW',
          label: 'AROW'
        }
      ]
    }

  }, // data


  computed: {

    // XOL-552: the Backhaul-category org that shares the RTB telco's name
    // (actionData.organisation is appended by the API), or null (e.g. DNB) — the
    // "+ Backhaul" Provider default. NOTE: getConfig() returns a module-level,
    // non-reactive object, so this computed caches the first value it computes
    // (it does not re-evaluate as the config hydrates). That is acceptable: the
    // config loads at boot and is cached in sessionStorage well before an RTB
    // can be opened.
    telcoBackhaulTwinId () {
      return findBackhaulTwinId(this.getConfig().orgGroups, this.actionData.organisation ? this.actionData.organisation.name : null)
    },

    disabled () {
      return !this.canWriteStep() || this.isViewOnly() || this.readOnly
    },

    // XOL-526 (UX §2.4, U-I): Progress Remarks is the counterparty's own
    // party-2 STEP row — Tower Co's own row (party 1) is a different object
    // and is never shared, so it gets no note/stamp.
    progressRemarksStamp () {
      return (!this.isTowerCo() && this.step) ? this.editStamp(this.step.state, 'remarks') : null
    },

    progressRemarksNote () {
      return (this.canWriteOwnStep(this.step) && !this.isTowerCo() && !this.readOnly && this.hasDistinctProvider())
        ? this.sharedFieldNote('edit these remarks')
        : null
    }

  },


  created () {

    if (this.step) {

      // XOL-534 (reviewer R2): unconditional baseline, set BEFORE the
      // possible findStep() fetch — see step1.vue's created() for why.
      // setStep() re-captures it fresh once the fetch resolves.
      this.initialRemarks = this.step.remarks

      // XOL-526 (S5, D9): was `canWriteStep()` — a counterparty may now
      // write its own party-2 row too.
      if ((this.canWriteStep() || this.canWriteOwnStep(this.step)) && !this.readOnly) {
        this.findStep(this.stepData.id, this.setStep)
      }

      if (!this.readOnly) {

        if (!this.step.state.hasOwnProperty('saved')) {
          this.step.state.saved = false
        }

        this.step2p2 = this.findWorkflowStep(2, 2, this.rowData.steps)

        if (this.step && this.step.stepNo === 3 && this.step.partyNo === 1) {
          this.step3p1 = this.step
        } else {
          this.step3p1 = this.findWorkflowStep(3, 1, this.rowData.steps)
        }

        this.fixExistingEquipment() // just in case.

      }

    }

  },



  beforeDestroy () {

    if (!this.readOnly && this.step) {
      this.$emit('updated', this.step)

      if (this.canWriteStep()) {
        this.saveStep(this.step, null, this.actionData, true)
      } else if (this.canWriteOwnStep(this.step) && this.step.remarks !== this.initialRemarks) {
        // XOL-526 (S5, D9): dirty-only, own-row remarks. Never `saveAction`
        // — Tower Co's saveStep(…, true) resends the whole actionData, which
        // a counterparty must never trigger.
        this.saveOwnStep(this.step, {remarks: this.step.remarks})
      }
    }
  }

}
</script>
